/**
 * Maps URL slugs for the best-companies dimension pages to Algolia filter
 * expressions on the 'companies' index.
 *
 * Two dimension types:
 *  - Industry: filters on `industryEn`
 *  - City:     filters on `registryCity`
 */

export type DimensionType = 'industry' | 'city';

export interface DimensionConfig {
  type: DimensionType;
  label: string; // display label, e.g. "Technology"
  algoliaFilter: string; // Algolia filter expression
  metaTitleSuffix: string; // appended after "Best Employers in …"
  metaDescriptionFragment: string;
}

const DIMENSION_MAP: Record<string, DimensionConfig> = {
  // ── Industries ─────────────────────────────────────────────────────────────
  technology: {
    type: 'industry',
    label: 'Technology',
    algoliaFilter: 'industryEn:"Technology"',
    metaTitleSuffix: 'Technology',
    metaDescriptionFragment: 'the technology sector in Finland',
  },
  finance: {
    type: 'industry',
    label: 'Finance & Banking',
    algoliaFilter: 'industryEn:"Finance"',
    metaTitleSuffix: 'Finance & Banking',
    metaDescriptionFragment: 'finance and banking in Finland',
  },
  healthcare: {
    type: 'industry',
    label: 'Healthcare',
    algoliaFilter: 'industryEn:"Healthcare"',
    metaTitleSuffix: 'Healthcare',
    metaDescriptionFragment: 'the healthcare sector in Finland',
  },
  retail: {
    type: 'industry',
    label: 'Retail & E-commerce',
    algoliaFilter: 'industryEn:"Retail"',
    metaTitleSuffix: 'Retail & E-commerce',
    metaDescriptionFragment: 'retail and e-commerce in Finland',
  },
  manufacturing: {
    type: 'industry',
    label: 'Manufacturing & Engineering',
    algoliaFilter: 'industryEn:"Manufacturing"',
    metaTitleSuffix: 'Manufacturing & Engineering',
    metaDescriptionFragment: 'manufacturing and engineering in Finland',
  },
  consulting: {
    type: 'industry',
    label: 'Consulting & Professional Services',
    algoliaFilter: 'industryEn:"Consulting"',
    metaTitleSuffix: 'Consulting',
    metaDescriptionFragment: 'consulting and professional services in Finland',
  },
  education: {
    type: 'industry',
    label: 'Education & Research',
    algoliaFilter: 'industryEn:"Education"',
    metaTitleSuffix: 'Education & Research',
    metaDescriptionFragment: 'education and research organisations in Finland',
  },
  logistics: {
    type: 'industry',
    label: 'Logistics & Transport',
    algoliaFilter: 'industryEn:"Logistics"',
    metaTitleSuffix: 'Logistics & Transport',
    metaDescriptionFragment: 'logistics and transport companies in Finland',
  },
  media: {
    type: 'industry',
    label: 'Media & Marketing',
    algoliaFilter: 'industryEn:"Media"',
    metaTitleSuffix: 'Media & Marketing',
    metaDescriptionFragment: 'media and marketing companies in Finland',
  },
  // ── Cities ──────────────────────────────────────────────────────────────────
  helsinki: {
    type: 'city',
    label: 'Helsinki',
    algoliaFilter: 'registryCity:"Helsinki"',
    metaTitleSuffix: 'Helsinki',
    metaDescriptionFragment: 'Helsinki',
  },
  espoo: {
    type: 'city',
    label: 'Espoo',
    algoliaFilter: 'registryCity:"Espoo"',
    metaTitleSuffix: 'Espoo',
    metaDescriptionFragment: 'Espoo',
  },
  tampere: {
    type: 'city',
    label: 'Tampere',
    algoliaFilter: 'registryCity:"Tampere"',
    metaTitleSuffix: 'Tampere',
    metaDescriptionFragment: 'Tampere',
  },
  turku: {
    type: 'city',
    label: 'Turku',
    algoliaFilter: 'registryCity:"Turku"',
    metaTitleSuffix: 'Turku',
    metaDescriptionFragment: 'Turku',
  },
  oulu: {
    type: 'city',
    label: 'Oulu',
    algoliaFilter: 'registryCity:"Oulu"',
    metaTitleSuffix: 'Oulu',
    metaDescriptionFragment: 'Oulu',
  },
  vantaa: {
    type: 'city',
    label: 'Vantaa',
    algoliaFilter: 'registryCity:"Vantaa"',
    metaTitleSuffix: 'Vantaa',
    metaDescriptionFragment: 'Vantaa',
  },
  jyvaskyla: {
    type: 'city',
    label: 'Jyväskylä',
    algoliaFilter: 'registryCity:"Jyväskylä"',
    metaTitleSuffix: 'Jyväskylä',
    metaDescriptionFragment: 'Jyväskylä',
  },
  lahti: {
    type: 'city',
    label: 'Lahti',
    algoliaFilter: 'registryCity:"Lahti"',
    metaTitleSuffix: 'Lahti',
    metaDescriptionFragment: 'Lahti',
  },
  kuopio: {
    type: 'city',
    label: 'Kuopio',
    algoliaFilter: 'registryCity:"Kuopio"',
    metaTitleSuffix: 'Kuopio',
    metaDescriptionFragment: 'Kuopio',
  },
  pori: {
    type: 'city',
    label: 'Pori',
    algoliaFilter: 'registryCity:"Pori"',
    metaTitleSuffix: 'Pori',
    metaDescriptionFragment: 'Pori',
  },
  lappeenranta: {
    type: 'city',
    label: 'Lappeenranta',
    algoliaFilter: 'registryCity:"Lappeenranta"',
    metaTitleSuffix: 'Lappeenranta',
    metaDescriptionFragment: 'Lappeenranta',
  },
  kotka: {
    type: 'city',
    label: 'Kotka',
    algoliaFilter: 'registryCity:"Kotka"',
    metaTitleSuffix: 'Kotka',
    metaDescriptionFragment: 'Kotka',
  },
  joensuu: {
    type: 'city',
    label: 'Joensuu',
    algoliaFilter: 'registryCity:"Joensuu"',
    metaTitleSuffix: 'Joensuu',
    metaDescriptionFragment: 'Joensuu',
  },
  rovaniemi: {
    type: 'city',
    label: 'Rovaniemi',
    algoliaFilter: 'registryCity:"Rovaniemi"',
    metaTitleSuffix: 'Rovaniemi',
    metaDescriptionFragment: 'Rovaniemi',
  },
  seinajoki: {
    type: 'city',
    label: 'Seinäjoki',
    algoliaFilter: 'registryCity:"Seinäjoki"',
    metaTitleSuffix: 'Seinäjoki',
    metaDescriptionFragment: 'Seinäjoki',
  },
  vaasa: {
    type: 'city',
    label: 'Vaasa',
    algoliaFilter: 'registryCity:"Vaasa"',
    metaTitleSuffix: 'Vaasa',
    metaDescriptionFragment: 'Vaasa',
  },
  porvoo: {
    type: 'city',
    label: 'Porvoo',
    algoliaFilter: 'registryCity:"Porvoo"',
    metaTitleSuffix: 'Porvoo',
    metaDescriptionFragment: 'Porvoo',
  },
  hameenlinna: {
    type: 'city',
    label: 'Hämeenlinna',
    algoliaFilter: 'registryCity:"Hämeenlinna"',
    metaTitleSuffix: 'Hämeenlinna',
    metaDescriptionFragment: 'Hämeenlinna',
  },
  mikkeli: {
    type: 'city',
    label: 'Mikkeli',
    algoliaFilter: 'registryCity:"Mikkeli"',
    metaTitleSuffix: 'Mikkeli',
    metaDescriptionFragment: 'Mikkeli',
  },
  kokkola: {
    type: 'city',
    label: 'Kokkola',
    algoliaFilter: 'registryCity:"Kokkola"',
    metaTitleSuffix: 'Kokkola',
    metaDescriptionFragment: 'Kokkola',
  },
  kajaani: {
    type: 'city',
    label: 'Kajaani',
    algoliaFilter: 'registryCity:"Kajaani"',
    metaTitleSuffix: 'Kajaani',
    metaDescriptionFragment: 'Kajaani',
  },
  rauma: {
    type: 'city',
    label: 'Rauma',
    algoliaFilter: 'registryCity:"Rauma"',
    metaTitleSuffix: 'Rauma',
    metaDescriptionFragment: 'Rauma',
  },
  lohja: {
    type: 'city',
    label: 'Lohja',
    algoliaFilter: 'registryCity:"Lohja"',
    metaTitleSuffix: 'Lohja',
    metaDescriptionFragment: 'Lohja',
  },
  nurmijarvi: {
    type: 'city',
    label: 'Nurmijärvi',
    algoliaFilter: 'registryCity:"Nurmijärvi"',
    metaTitleSuffix: 'Nurmijärvi',
    metaDescriptionFragment: 'Nurmijärvi',
  },
};

export function getDimensionConfig(slug: string): DimensionConfig | null {
  return DIMENSION_MAP[slug.toLowerCase()] ?? null;
}

/** All configured dimensions — used to build the hub page. */
export const ALL_DIMENSIONS = Object.entries(DIMENSION_MAP).map(([slug, config]) => ({
  slug,
  ...config,
}));

export const INDUSTRY_DIMENSIONS = ALL_DIMENSIONS.filter(d => d.type === 'industry');
export const CITY_DIMENSIONS = ALL_DIMENSIONS.filter(d => d.type === 'city');
